import { $config } from '@client/core/atoms/config.js';
import { slotMetadataStore } from '@client/core/utils/pulse/gamDataStore.js';
import pulse from '@finn-no/pulse-sdk';
import type { TrackerData } from '@finn-no/pulse-sdk/dist/types.js';
import { debugLog } from '@schibsted-nmp/advertising-shared';

type PulseAdEvent = {
  intent: TrackerData['intent'];
  name: TrackerData['name'];
  object: Omit<
    TrackerData['object'],
    'id' | 'adType' | 'adPosition' | 'adCreativ'
  > & {
    adOrderId?: number;
    lineItemId?: number;
    adVendor?: number;
    creativeId?: number;
  };
  vertical: TrackerData['vertical'];
};

type WithoutNullish<T> = { [K in keyof T]?: NonNullable<T[K]> };

const removeNullish = <T extends Record<string, unknown>>(
  obj: T
): WithoutNullish<T> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value != null)
  ) as WithoutNullish<T>;
};

export function trackAdViewable(slot: googletag.Slot) {
  const gamData = slotMetadataStore.get(slot);

  if (!gamData) return;

  const { lineItemId, creativeId, campaignId, advertiserId } = gamData;
  const config = $config.get();

  const vertical: TrackerData['vertical'] = config?.vertical
    ? {
        name: String(config.vertical),
        ...(config.subvertical && { subVertical: String(config.subvertical) })
      }
    : undefined;

  const pulseAdEvent: PulseAdEvent = {
    intent: 'Show',
    name: 'Google ad viewed',
    object: removeNullish({
      type: 'DisplayAd',
      adOrderId: campaignId,
      lineItemId,
      adVendor: advertiserId,
      creativeId
    }),
    vertical
  };

  try {
    pulse.trackEvent(pulseAdEvent as TrackerData);
  } catch (error) {
    debugLog('[pulse] failed to track ad_viewable', error);
  } finally {
    slotMetadataStore.clear(slot);
  }
}
